'use client';

import { useEffect, useState } from 'react';

/**
 * Static UI element heights (in pixels)
 * These are approximate values for calculating available image height
 */
const STATIC_HEIGHTS = {
  /** Mobile: header + tabs + padding */
  mobile: 136,
  /** Desktop: similar but more horizontal space available */
  desktop: 100,
};

/**
 * Reserve percentage of viewport for controls below the image
 */
const RESERVE_PERCENTAGE = 0.1;

interface UseImageConstraintsResult {
  /** CSS max-height value (e.g., "calc(100vh - 200px)") */
  maxHeightCss: string;
  /** Numeric max height in pixels (for legacy compatibility) */
  maxHeightPx: number | null;
  /** Whether constraints are ready (client-side calculated) */
  isReady: boolean;
}

/**
 * Hook to calculate image height constraints for viewport-fitting images.
 *
 * Returns CSS-based constraints instead of pixel values to avoid
 * layout thrashing caused by ResizeObserver cycles.
 *
 * @example
 * ```tsx
 * function ImageContainer() {
 *   const { maxHeightCss } = useImageConstraints();
 *
 *   return (
 *     <div style={{
 *       aspectRatio: '4/3',
 *       maxHeight: maxHeightCss,
 *       width: 'auto',
 *       maxWidth: '100%',
 *       margin: '0 auto',
 *     }}>
 *       <Image ... />
 *     </div>
 *   );
 * }
 * ```
 */
export function useImageConstraints(): UseImageConstraintsResult {
  const [state, setState] = useState<UseImageConstraintsResult>({
    maxHeightCss: '70vh', // SSR fallback - reasonable default
    maxHeightPx: null,
    isReady: false,
  });

  useEffect(() => {
    const calculate = () => {
      const vh = window.innerHeight;
      const staticHeight =
        window.innerWidth < 768
          ? STATIC_HEIGHTS.mobile
          : STATIC_HEIGHTS.desktop;
      const reserve = Math.round(vh * RESERVE_PERCENTAGE);
      const totalOffset = staticHeight + reserve;

      // Use calc() for CSS - responsive to viewport changes
      const maxHeightCss = `calc(100vh - ${totalOffset}px)`;

      // Numeric value for legacy compatibility
      const maxHeightPx = Math.max(200, vh - totalOffset);

      setState({
        maxHeightCss,
        maxHeightPx,
        isReady: true,
      });
    };

    calculate();

    window.addEventListener('resize', calculate);
    window.addEventListener('orientationchange', calculate);

    return () => {
      window.removeEventListener('resize', calculate);
      window.removeEventListener('orientationchange', calculate);
    };
  }, []);

  return state;
}
